import { useCallback } from "react";

export interface SessionRate {
  id: string;
  amount_pence: number;
  duration_minutes: number;
  currency: string;
  sort_order: number;
  badge: string | null;
  primary_for_idle: boolean;
  is_active: boolean;
}

export interface TierDraft {
  id?: string;
  amount: string;
  duration: string;
  badge: string;
  primary_for_idle: boolean;
}

export function rateToTierDraft(rate: SessionRate): TierDraft {
  return {
    id: rate.id,
    amount: (rate.amount_pence / 100).toFixed(2),
    duration: String(rate.duration_minutes),
    badge: rate.badge ?? "",
    primary_for_idle: rate.primary_for_idle,
  };
}

export function emptyTierDraft(sortOrder: number): TierDraft {
  return {
    amount: "",
    duration: "",
    badge: "",
    primary_for_idle: sortOrder === 0,
  };
}

export function tiersToRatePayload(tiers: TierDraft[]) {
  return tiers.map((tier, index) => {
    const amountPence = Math.round(parseFloat(tier.amount) * 100);
    const durationMinutes = parseInt(tier.duration, 10);

    if (isNaN(amountPence) || amountPence <= 0) {
      throw new Error(`Tier ${index + 1}: Amount must be greater than £0.00`);
    }
    if (isNaN(durationMinutes) || durationMinutes <= 0) {
      throw new Error(`Tier ${index + 1}: Duration must be at least 1 minute`);
    }

    return {
      ...(tier.id ? { id: tier.id } : {}),
      amount_pence: amountPence,
      duration_minutes: durationMinutes,
      sort_order: index,
      badge: tier.badge || null,
      primary_for_idle: tier.primary_for_idle,
    };
  });
}

export function summarizeRates(
  rates: Array<{ amount_pence: number; duration_minutes: number; currency?: string }>,
): string {
  if (rates.length === 0) return "(no rates)";
  return rates
    .map((r) => {
      const pounds = (r.amount_pence / 100).toFixed(2);
      return `£${pounds}/${r.duration_minutes}m`;
    })
    .join(" · ");
}

export function formatRevisionAction(action: string): string {
  switch (action) {
    case "fleet_rates_updated":
      return "Fleet rates updated";
    case "venue_override_enabled":
      return "Venue override enabled";
    case "venue_override_disabled":
      return "Venue override disabled";
    case "venue_rates_updated":
      return "Venue rates updated";
    default:
      return action;
  }
}

interface TierEditorProps {
  tiers: TierDraft[];
  onChange: (tiers: TierDraft[]) => void;
  disabled?: boolean;
}

export function TierEditor({ tiers, onChange, disabled }: TierEditorProps) {
  const updateTier = useCallback(
    (index: number, updates: Partial<TierDraft>) => {
      onChange(tiers.map((t, i) => (i === index ? { ...t, ...updates } : t)));
    },
    [tiers, onChange],
  );

  const setPrimaryTier = useCallback(
    (index: number) => {
      onChange(tiers.map((t, i) => ({ ...t, primary_for_idle: i === index })));
    },
    [tiers, onChange],
  );

  const addTier = useCallback(() => {
    onChange([...tiers, { ...emptyTierDraft(tiers.length), primary_for_idle: false }]);
  }, [tiers, onChange]);

  const removeTier = useCallback(
    (index: number) => {
      const next = tiers.filter((_, i) => i !== index);
      if (next.length > 0 && !next.some((t) => t.primary_for_idle)) {
        next[0] = { ...next[0], primary_for_idle: true };
      }
      onChange(next);
    },
    [tiers, onChange],
  );

  return (
    <div className="space-y-4">
      {tiers.map((tier, index) => (
        <div
          key={tier.id ?? `new-${index}`}
          className="bg-surface-raised border border-border rounded-lg p-4 space-y-3"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-text-secondary">Tier {index + 1}</span>
            <button
              type="button"
              onClick={() => removeTier(index)}
              disabled={disabled || tiers.length <= 1}
              className="text-sm text-red-400 hover:text-red-300 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Remove
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Amount (£)</label>
              <input
                type="number"
                value={tier.amount}
                onChange={(e) => updateTier(index, { amount: e.target.value })}
                placeholder="5.00"
                step="0.01"
                min="0.01"
                required
                disabled={disabled}
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Duration (minutes)</label>
              <input
                type="number"
                value={tier.duration}
                onChange={(e) => updateTier(index, { duration: e.target.value })}
                placeholder="30"
                step="1"
                min="1"
                required
                disabled={disabled}
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors disabled:opacity-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Badge</label>
              <select
                value={tier.badge}
                onChange={(e) => updateTier(index, { badge: e.target.value })}
                disabled={disabled}
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors disabled:opacity-50"
              >
                <option value="">None</option>
                <option value="popular">Popular</option>
                <option value="best_value">Best Value</option>
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="primary_for_idle"
                  checked={tier.primary_for_idle}
                  onChange={() => setPrimaryTier(index)}
                  disabled={disabled}
                  className="accent-accent"
                />
                <span className="text-sm text-text-secondary">Default on idle screen</span>
              </label>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addTier}
        disabled={disabled}
        className="w-full py-2 text-sm rounded-md border border-dashed border-border text-text-muted hover:text-text-secondary hover:border-text-muted transition-colors disabled:opacity-50"
      >
        + Add Tier
      </button>
    </div>
  );
}

export function TierReadonlyList({ tiers }: { tiers: TierDraft[] }) {
  return (
    <div className="space-y-4">
      {tiers.map((tier, index) => (
        <div
          key={tier.id ?? `tier-${index}`}
          className="bg-surface-raised border border-border rounded-lg p-4 space-y-2"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-text-secondary">Tier {index + 1}</span>
            {tier.primary_for_idle && (
              <span className="text-xs text-accent">Default on idle screen</span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-text-muted">Amount:</span>{" "}
              <span className="text-text-primary">£{tier.amount}</span>
            </div>
            <div>
              <span className="text-text-muted">Duration:</span>{" "}
              <span className="text-text-primary">{tier.duration} minutes</span>
            </div>
          </div>
          {tier.badge && (
            <div className="text-sm">
              <span className="text-text-muted">Badge:</span>{" "}
              <span className="text-text-primary capitalize">{tier.badge.replace("_", " ")}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export interface PricingRevision {
  id: string;
  action: string;
  rates: SessionRate[];
  changed_by_email: string | null;
  created_at: string;
}

export function PricingHistoryList({ history }: { history: PricingRevision[] }) {
  function formatWhen(iso: string): string {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <section className="border border-border rounded-md p-4 max-w-2xl">
      <h2 className="text-sm text-text-secondary mb-3">Price change history</h2>
      {history.length > 0 ? (
        <ul className="space-y-2">
          {history.map((entry) => (
            <li
              key={entry.id}
              className="border-b border-border last:border-0 pb-2 last:pb-0"
            >
              <p className="text-sm text-text-primary">{formatRevisionAction(entry.action)}</p>
              <p className="text-xs text-text-muted font-mono">{summarizeRates(entry.rates)}</p>
              <p className="text-xs text-text-muted">
                {formatWhen(entry.created_at)} · {entry.changed_by_email ?? "unknown"}
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-text-muted">No price changes recorded yet.</p>
      )}
    </section>
  );
}
